import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Shield, Lock, Mail, Phone, MapPin, Calendar, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  
  const sections = [
    {
      title: "Information We Collect",
      icon: Shield,
      content: [
        {
          subtitle: "Personal Information",
          text: "When you book a table, create an account, or check in at Scratch Unleashed, we may collect: Full name, Phone number, Email address, Date of birth (for age verification and birthday offers), Payment information (processed securely through our payment partners)."
        },
        {
          subtitle: "Automatically Collected Information",
          text: "We automatically collect certain information when you visit our website: IP address, Browser type and version, Device information, Pages visited and time spent, Referral source."
        },
        {
          subtitle: "Communication Data",
          text: "If you subscribe to our marketing communications, we collect: Phone number for SMS/WhatsApp campaigns, Email address for newsletters, Communication preferences and opt-in status."
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: Lock,
      content: [
        {
          subtitle: "Service Delivery",
          text: "We use your information to: Process table bookings and reservations, Manage check-ins and entry, Process payments securely, Provide customer support, Send booking confirmations and event details."
        },
        {
          subtitle: "Marketing Communications",
          text: "With your explicit consent, we send: WhatsApp messages about upcoming events and special offers, SMS notifications for exclusive deals, Email newsletters about DJ nights and parties, Birthday offers and personalized promotions. You can opt-out anytime by replying STOP to SMS, clicking unsubscribe in emails, or contacting us directly."
        },
        {
          subtitle: "Business Operations",
          text: "We use aggregated data to: Improve our services and customer experience, Analyze attendance patterns and preferences, Plan events and capacity management, Comply with legal obligations (age verification, licensing)."
        }
      ]
    },
    {
      title: "Data Sharing & Third Parties",
      icon: MapPin,
      content: [
        {
          subtitle: "We Share Data With",
          text: "Payment Processors: Secure payment gateways for transaction processing. SMS/WhatsApp Providers: MSG91 and Meta WhatsApp Cloud API for communication. Analytics Services: Google Analytics for website performance (anonymized). Legal Authorities: When required by law or to protect rights and safety."
        },
        {
          subtitle: "We DO NOT",
          text: "Sell your personal information to third parties. Share your data for unrelated marketing purposes. Send you unsolicited communications without consent."
        }
      ]
    },
    {
      title: "WhatsApp & SMS Marketing",
      icon: Phone,
      content: [
        {
          subtitle: "Consent-Based Messaging",
          text: "By opting in to our WhatsApp/SMS marketing: You agree to receive promotional messages about events, offers, and updates. Message frequency may vary (typically 2-4 messages per month). Standard data rates may apply as per your mobile carrier."
        },
        {
          subtitle: "Your Rights",
          text: "Opt-out anytime: Reply STOP to any SMS, Block our WhatsApp Business number, Email us at privacy@scratchjsr.com, Update preferences in your account settings. We will process opt-out requests within 48 hours."
        }
      ]
    },
    {
      title: "Data Security",
      icon: Lock,
      content: [
        {
          subtitle: "How We Protect Your Data",
          text: "We implement industry-standard security measures: SSL/TLS encryption for data transmission, Secure servers with regular backups, Access controls and authentication, Regular security audits, Payment data is never stored on our servers (handled by PCI-compliant processors)."
        },
        {
          subtitle: "Data Retention",
          text: "We retain your data for: Active accounts: Until you request deletion, Inactive accounts: Up to 2 years, Transaction records: 7 years (legal requirement), Marketing data: Until you opt-out or request deletion."
        }
      ]
    },
    {
      title: "Your Rights & Choices",
      icon: Shield,
      content: [
        {
          subtitle: "You Have the Right To",
          text: "Access: Request a copy of your personal data. Correct: Update inaccurate or incomplete information. Delete: Request deletion of your account and data (\"Right to be Forgotten\"). Port: Request data in a machine-readable format. Object: Opt-out of marketing communications anytime. Restrict: Limit how we process your data."
        },
        {
          subtitle: "How to Exercise Your Rights",
          text: "Email: privacy@scratchjsr.com. Phone: +917870369666. Visit: Scratch Unleashed, Jamshedpur. We will respond to requests within 30 days."
        }
      ]
    },
    {
      title: "Cookies & Tracking",
      icon: Calendar,
      content: [
        {
          subtitle: "Cookie Usage",
          text: "We use cookies to: Remember your preferences and login status, Analyze website traffic and user behavior, Deliver personalized content and ads, Improve website functionality. You can manage cookie preferences in your browser settings."
        }
      ]
    },
    {
      title: "Children's Privacy",
      icon: Shield,
      content: [
        {
          text: "Scratch Unleashed is an 18+ venue. We do not knowingly collect information from individuals under 18 years of age. Age verification is required for entry and booking. If we discover we've collected data from a minor, we will delete it immediately."
        }
      ]
    },
    {
      title: "Changes to Privacy Policy",
      icon: Calendar,
      content: [
        {
          text: "We may update this Privacy Policy periodically. Changes will be: Posted on this page with an updated \"Last Modified\" date, Notified via email or WhatsApp for significant changes, Effective immediately upon posting unless stated otherwise. Continued use of our services after changes constitutes acceptance."
        }
      ]
    },
    {
      title: "Contact Us",
      icon: Mail,
      content: [
        {
          subtitle: "Data Protection Officer",
          text: "For privacy-related questions or requests: Email: privacy@scratchjsr.com, Phone: +917870369666, WhatsApp: +917870369666, Address: Scratch Unleashed, Jamshedpur, Jharkhand 831001, India. We are committed to resolving privacy concerns promptly and transparently."
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Scratch Unleashed - Your Data, Your Rights</title>
        <meta name="description" content="Learn how Scratch Unleashed protects your privacy. Comprehensive information on data collection, WhatsApp marketing, SMS communications, and your rights." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://scratchjsr.com/privacy-policy" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Privacy Policy | Scratch Unleashed" />
        <meta property="og:description" content="Your privacy matters. Learn how we protect your data and respect your rights." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://scratchjsr.com/privacy-policy" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Privacy Policy | Scratch Unleashed" />
        <meta name="twitter:description" content="Your privacy matters. Learn how we protect your data." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
        {/* Back Button */}
        <div className="fixed top-6 left-6 z-50">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="gap-2 text-[#B9B9B9] hover:text-[#F2F2F2]"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </div>
        
        {/* Hero */}
        <section className="relative pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6" style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.25)' }}>
              <Shield size={32} style={{ color: '#D4A843' }} />
            </div>
            <h1 className="font-['EB_Garamond'] text-5xl md:text-6xl font-bold text-[#F2F2F2] mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-[#B9B9B9] max-w-2xl mx-auto">
              Your privacy is important to us. This policy explains how Scratch Unleashed collects, uses, and protects your personal information.
            </p>
            <p className="text-sm text-[#6B6B6B] mt-4">
              <strong>Last Updated:</strong> January 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-20 px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {sections.map((section, idx) => (
              <div key={idx} className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                    <section.icon size={20} style={{ color: '#D4A843' }} />
                  </div>
                  <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                    {section.title}
                  </h2>
                </div>
                
                <div className="space-y-6">
                  {section.content.map((item, i) => (
                    <div key={i}>
                      {item.subtitle && (
                        <h3 className="text-lg font-semibold text-[#D4A843] mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-[#B9B9B9] leading-relaxed whitespace-pre-line">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="mailto:privacy@scratchjsr.com"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#D4A843] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <Mail size={24} className="mx-auto mb-3 text-[#D4A843]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">Email Us</h3>
                <p className="text-sm text-[#B9B9B9]">privacy@scratchjsr.com</p>
              </a>

              <a
                href="https://wa.me/917870369666"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#25D366] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <Phone size={24} className="mx-auto mb-3 text-[#25D366]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">WhatsApp</h3>
                <p className="text-sm text-[#B9B9B9]">+91 7870 369 666</p>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-[rgba(255,255,255,0.07)] py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-sm text-[#6B6B6B]">
              © {new Date().getFullYear()} Scratch Unleashed. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <button onClick={() => navigate('/contact')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">
                Contact Us
              </button>
              <span className="text-[#6B6B6B]">·</span>
              <a href="https://scratchjsr.com" className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">
                scratchjsr.com
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
